import React, { useContext, useEffect, useState } from 'react'
import classNames from 'classnames'
import Products from '../Products/Products'
import { Pagination } from 'client/common/components/Pagination/Pagination'
import { useActionProps } from '../../hooks/useActionProps'
import { onPageChange } from '../../store/actions/pageActions'
import FilterButton from '../FilterButton/FilterButton'
import { FiltersSide } from '../Filters/FiltersSide/FiltersSide'
import Sorting from '../Sorting/Sorting'
import { TranslateContext } from 'client/common/translator'
import { getPaginationProps } from '../../selectors/paginationProps'
import SeoWidgetContainer from 'client/fragments/listerFragment/components/SeoWidgetContainer/SeoWidgetContainer'
import LegalInfo from 'client/fragments/listerFragment/components/LegalInfo/LegalInfo'
import ListerHeader from 'client/fragments/listerFragment/components/ListerHeader'
import LastViewedContainer from 'client/fragments/listerFragment/components/LastViewedContainer'
import { ContentSwitcher } from 'client/fragments/listerFragment/components/ContentSwitcher/ContentSwitcher'
import SoftContentContainer from 'client/fragments/listerFragment/components/SoftContentContainer/SoftContentContainer'
import { useSelector } from 'react-redux'
import { Container, TABLET, isMobile, Text } from 'chemistry'
import { isEmptyObject } from 'client/common/utils'
import { LinksToCollectionPages } from '../LinksToCollectionPages'
import CategoryBanner from 'client/fragments/listerFragment/components/Banner/CategoryBanner'
import { Divider } from 'client/common/components/Divider'
import YellowWarningIcon from 'client/fragments/listerFragment/components/YellowWarningIcon'
import { FiltersTop } from '../Filters/FiltersTop/FiltersTop'
import { useFilters } from '../Filters/useFilters'
import {
  SHOW_TOP_SUBCATEGORIES,
  SHOW_SOLD_BY_PRODUCT_CARDS,
  SHOW_MULTI_AVAILABILITY,
  SHOW_NEW_CARDS_GRID,
  SEARCH_REFINED_CONTENT_PAGE,
  SHOW_OWN_BRAND_COMMUNICATION
} from 'common/constants'
import TopSubcategories from '../TopSubcategories/TopSubcategories'
import {
  isFlagInGA,
  pushFlagToDatalayer,
  pushToDatalayer,
  pushTrackedFlagToDataLayer
} from '../../../../common/gtmEvents'
import AvailabilitySwitcher from 'client/fragments/listerFragment/components/AvailabilitySwitcher/AvailabilitySwitcher'

const ProductsContainer = () => {
  const t = useContext(TranslateContext)

  const products = useSelector(({ data }) => data.products)
  const lang = useSelector(({ data }) => data?.lang)
  const format = useSelector(({ data }) => data?.format)
  const filters = useSelector(({ data }) => data.filters)
  const page = useSelector(({ data }) => data.page)
  const mode = useSelector(({ data }) => data.mode)
  const viewSize = useSelector(({ data }) => data.viewSize)
  const ids = useSelector(({ data }) => data.ids)
  const zeroResults = useSelector(({ data }) => data.zeroResults)
  const isInApp = useSelector(({ data }) => data.isInApp)
  const linksToCollectionPages = useSelector(({ data }) => data.linksToCollectionPages)
  const flagsToGA = useSelector(store => store.data?.flags?.flagsToGA)
  const searchTerm = useSelector(store => store.data?.term)
  const categoryCode = useSelector(store => store.data?.categoryCode)
  const categoryInfo = useSelector(it => it.data.categoryInfo)
  const showTopSubcategories = useSelector(({ data }) => data?.flags?.showTopSubcategories)
  const categoryCfInfo = useSelector(it => it.data.categoryCfInfo)
  const breadcrumbs = useSelector(state => state.data.breadcrumbs)
  const isCollectionPage = useSelector(({ data }) => !!data.collectionId)
  const [filtersToggled, setFiltersToggled] = useState(false)
  const toggleFilters = () => setFiltersToggled(prevState => !prevState)
  const filterProcessedData = useFilters({ filters, t })
  const experimentData = useSelector(({ data }) => data.experimentData)
  const searchTopSubcategories = useSelector(({ data }) => data.searchTopSubcategories)
  const pageType = useSelector(({ data }) => data.pageType)

  const { quantity, collection } = products
  const hasOwnBrand = collection?.some?.(product => product.ownBrand)
  const paginationProps = getPaginationProps({ page, quantity, t, filters, viewSize, breadcrumbs })

  const isSoftContent = mode !== 'products'
  const showSorting = !isSoftContent && !ids
  const shouldAddExtraBottomSpacing = isInApp
  const isExperimentDataPresent = experimentData && !isEmptyObject(experimentData)

  const showTopSubcategoriesSearch =
    !isSoftContent && searchTerm && showTopSubcategories && searchTopSubcategories.length > 0

  useEffect(() => {
    if (isExperimentDataPresent && !isSoftContent && (categoryCode || searchTerm)) {
      pushToDatalayer({
        event: 'constructor_experiments_view',
        ...experimentData
      })
    }

    if (pageType === SEARCH_REFINED_CONTENT_PAGE && isFlagInGA({ flagsToGA, ldFlag: SHOW_TOP_SUBCATEGORIES })) {
      pushFlagToDatalayer({
        ldFlag: SHOW_TOP_SUBCATEGORIES,
        ldVariation: flagsToGA[SHOW_TOP_SUBCATEGORIES]
      }) // send event indicating A/B that test is running with appropriate variation
    }

    if (isFlagInGA({ flagsToGA, ldFlag: SHOW_NEW_CARDS_GRID })) {
      pushFlagToDatalayer({
        ldFlag: SHOW_NEW_CARDS_GRID,
        ldVariation: flagsToGA[SHOW_NEW_CARDS_GRID]
      })
    }
    if (isFlagInGA({ flagsToGA, ldFlag: SHOW_MULTI_AVAILABILITY })) {
      pushFlagToDatalayer({
        ldFlag: SHOW_MULTI_AVAILABILITY,
        ldVariation: flagsToGA[SHOW_MULTI_AVAILABILITY]
      })
    }
    if (hasOwnBrand && isFlagInGA({ flagsToGA, ldFlag: SHOW_OWN_BRAND_COMMUNICATION })) {
      pushFlagToDatalayer({
        ldFlag: SHOW_OWN_BRAND_COMMUNICATION,
        ldVariation: flagsToGA[SHOW_OWN_BRAND_COMMUNICATION]
      })
    }
    pushTrackedFlagToDataLayer({
      flagsToGA,
      ldFlag: SHOW_SOLD_BY_PRODUCT_CARDS,
      pageType: categoryCode ? 'productCategory' : 'searchResult'
    })
  }, [
    flagsToGA,
    searchTerm,
    categoryCode,
    experimentData,
    isSoftContent,
    isExperimentDataPresent,
    pageType,
    hasOwnBrand
  ])

  const containerProps = isCollectionPage ? { wrapper: { padding: 0 } } : { wrapper: { paddingInline: 0 } }

  const showAvailabilitySwitcher = !isSoftContent && isMobile()
  return (
    <main className='category-listing-page'>
      <Container {...containerProps}>
        <div
          className={classNames({
            mb4: shouldAddExtraBottomSpacing
          })}
        >
          <ListerHeader isSoftContent={isSoftContent} />
          {categoryCode && (
            <TopSubcategories
              categoryCode={categoryCode}
              searchTopSubcategories={searchTopSubcategories}
              filters={filters}
            />
          )}
          {!isSoftContent && !filtersToggled && (
            <FilterButton
              visible={filters.collection}
              onClick={toggleFilters}
              sortBy={page.sortBy}
              activeFilters={filters.active}
            />
          )}
          <FiltersSide
            filters={filters}
            filterProcessedData={filterProcessedData}
            filtersToggled={filtersToggled}
            setFiltersToggled={setFiltersToggled}
            showSorting={showSorting}
            disabled={isSoftContent}
          />
          <div
            style={{ gridArea: 'products-header' }}
            id='products-header'
            className='products-header flex flex-wrap justify-between'
          >
            <ContentSwitcher productsQuantity={quantity} />
            {showSorting && <Sorting id='sorting_main' disabled={isSoftContent} className={'hide-on-tablet'} />}
            {showAvailabilitySwitcher && (
              <AvailabilitySwitcher
                collection={filters?.collection}
                filterProcessedData={filterProcessedData}
                format={format}
                lang={lang}
              />
            )}
            <Divider
              sx={{
                display: isSoftContent ? 'block' : 'none',
                [TABLET]: {
                  display: showTopSubcategoriesSearch ? 'none' : 'block'
                }
              }}
            />
            {zeroResults && (
              <div className='mxd-zero-results-banner'>
                <YellowWarningIcon />
                {t('lister.search.range-zero-results')}
              </div>
            )}
          </div>
          {!isSoftContent && (
            <FiltersTop
              flagsToGA={flagsToGA}
              filters={filters}
              filterProcessedData={filterProcessedData}
              filtersToggled={filtersToggled}
              setFiltersToggled={setFiltersToggled}
              allFiltersText={t('lister.filters.top.all_filters')}
              categoryCode={categoryCode}
              lang={lang}
              hideDivider={showTopSubcategoriesSearch}
            />
          )}
          {showTopSubcategoriesSearch && (
            <div style={{ marginTop: '24px' }}>
              <Text fontSize='16px' fontWeight='700' lineHeight='24px' letterSpacing='0.5px' mb='-32px'>
                {t('lister.search.refine_search')}
              </Text>
              <TopSubcategories searchTopSubcategories={searchTopSubcategories} />
              <Divider width='100%' mb='24px' mt='0' />
            </div>
          )}
          <CategoryBanner />
          <Products visible={!isSoftContent} />
          <SoftContentContainer visible={isSoftContent} />
          <Pagination
            {...{
              ...paginationProps,
              visible: !isSoftContent,
              useActionProps,
              onPageChange
            }}
          />
          <LastViewedContainer />
          <SeoWidgetContainer categoryInfo={categoryInfo} categoryCfInfo={categoryCfInfo} />
          <LinksToCollectionPages title={t('lister.links.also_look_at')} links={linksToCollectionPages} />
          <LegalInfo />
        </div>
      </Container>
    </main>
  )
}

export default ProductsContainer
